import { RetainerDepositField } from "@/components/RetainerDepositField";
import { useEffect, useRef, useState } from "react";
import { api, apiErr, downloadFile } from "@/lib/api";
import { Loader, Card } from "@/components/ui-helpers";
import { StatusBadge } from "@/components/StatusBadge";
import { PaymentReceiptActions } from "@/components/PaymentReceiptActions";
import { PaymentPlanCard } from "@/components/PaymentPlan";
import { PaymentProofPicker, emptyProof, proofProblem, submitPayment, PaymentStateTag } from "@/components/PaymentProof";
import { PayLinkButtons } from "@/components/PayLinkButtons";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { LastChangedHint } from "@/components/ClientChangeHistory";
import { DuplicateWarning, useDuplicateCheck } from "@/components/DuplicateGuard";
import { FileSignature, Eye, Download, Receipt, DollarSign, AlertTriangle, Save, Plus, Minus, Trash2, Send, Copy, CheckCircle2, X } from "lucide-react";
import { FeeAdjustmentForm, removeFeeAdjustment } from "@/components/FeeAdjustment";

const money = (v, cur = "CAD") => (v === null || v === undefined ? "—" : `${Number(v) < 0 ? "−" : ""}${cur} $${Math.abs(Number(v)).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

// Type-to-search dropdown for picking a service from the master fee list.
function ServiceCombobox({ value, options, onSelect, placeholder, testid }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const boxRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  const filtered = options.filter((o) => o.toLowerCase().includes(q.trim().toLowerCase()));
  const label = value || placeholder || "Select a service…";
  return (
    <div className="relative mt-1" ref={boxRef} data-testid={testid}>
      <button type="button" data-testid={`${testid}-trigger`} onClick={() => { setOpen((o) => !o); setQ(""); }}
        className={`w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm text-left flex items-center justify-between gap-2 ${value ? "text-slate-900" : "text-slate-400"}`}>
        <span className="truncate">{label}</span>
        <span className="text-slate-400 shrink-0">▾</span>
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden">
          <input autoFocus data-testid={`${testid}-search`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Type to search services…"
            className="w-full px-3 py-2 border-b border-slate-100 text-sm outline-none" />
          <div className="max-h-64 overflow-y-auto py-1">
            {filtered.length === 0 ? <div className="px-3 py-3 text-xs text-slate-400">No matching service</div> :
              filtered.map((o) => (
                <button type="button" key={o} data-testid={`${testid}-option-${o}`} onClick={() => { onSelect(o); setOpen(false); }}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 ${o === value ? "bg-primary/5 font-semibold text-primary" : "text-slate-700"}`}>{o}</button>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}

const RF_LABELS = {
  CLIENT_FULL_NAME: "Client full name", CLIENT_ADDRESS: "Client address", SERVICE_NAME: "Service / Application",
  RETAINER_DATE: "Retainer date", PROFESSIONAL_FEE: "Professional fee (CDN $)", HST_RATE: "HST %",
  DEPOSIT_AMOUNT: "Retainer deposit (CDN $)", REMAINING_BALANCE: "Remaining balance (CDN $)", ADMIN_FEE: "Admin / refund fee (CAD $)",
};

// arraybuffer responses carry JSON errors as bytes — decode them for a readable toast
async function blobErr(e) {
  try {
    const buf = e?.response?.data;
    if (buf instanceof ArrayBuffer) {
      const j = JSON.parse(new TextDecoder().decode(buf));
      return typeof j.detail === "string" ? j.detail : JSON.stringify(j.detail);
    }
  } catch { /* fall through */ }
  return apiErr(e);
}

export function CaseFeesRetainer({ app, onAppChanged, view = "both" }) {
  const appId = app.id;
  const { user } = useAuth();
  const [csMode, setCsMode] = useState("admin");
  const [billingApproval, setBillingApproval] = useState(false);
  const isAdminUser = user?.role === "admin" || (user?.permissions || []).includes("manage_settings");
  const hasCaseEdit = user?.role === "admin" || (user?.permissions || []).includes("edit_cases");
  // Countersigning visibility follows the admin-chosen mode: admin-only, staff with case access, auto, or off.
  const canCountersign = csMode === "off" || csMode === "auto" ? false
    : csMode === "staff" ? hasCaseEdit : isAdminUser;
  const isAdmin = user?.role === "admin";
  // Non-admins only send an approval request when an admin has turned the setting on; otherwise they delete directly.
  const needsApproval = !isAdmin && billingApproval;
  const showFees = view === "both" || view === "fees";
  const showRetainer = view === "both" || view === "retainer";
  const [ctx, setCtx] = useState(null);
  const [master, setMaster] = useState([]);
  const [fields, setFields] = useState({});
  const [versions, setVersions] = useState([]);
  const [busy, setBusy] = useState("");
  const [previewLabel, setPreviewLabel] = useState("");
  const [legacyHtml, setLegacyHtml] = useState("");
  const [pdfUrl, setPdfUrl] = useState("");
  const [signLink, setSignLink] = useState(null);
  const [notes, setNotes] = useState("");
  const [invoicePreview, setInvoicePreview] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [fieldHist, setFieldHist] = useState({});

  const load = () => {
    api.get(`/applications/${appId}/retainer/context`).then((r) => { setCtx(r.data); setFields(r.data.fields); }).catch((e) => toast.error(apiErr(e)));
    api.get(`/applications/${appId}/retainers`).then((r) => setVersions(r.data)).catch(() => {});
    if (app.client_id) api.get(`/clients/${app.client_id}/field-history`).then((r) => setFieldHist(r.data.latest || {})).catch(() => {});
  };
  useEffect(() => { load(); api.get("/fees").then((r) => setMaster(r.data.fee_schedule || [])).catch(() => {}); api.get("/settings").then((r) => { setCsMode(r.data.countersign_mode || "admin"); setBillingApproval(!!r.data.billing_delete_approval); }).catch(() => {}); }, [appId, app.service_type]);

  if (!ctx) return <Loader />;
  const fees = ctx.fees;
  const cur = fees.currency || "CAD";
  const caseInvoice = fees.invoices?.[0];

  const changeService = async (service) => {
    try { await api.put(`/applications/${appId}`, { service_type: service }); toast.success("Service updated — fee retrieved from master list"); onAppChanged?.(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const createInvoice = async () => {
    setBusy("invoice");
    try { await api.post("/invoices", { application_id: appId, client_id: app.client_id }); toast.success("Invoice created from master fee"); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };
  // CLIENT_* merge fields are the client's own record — editing one here writes it back to the client profile
  const CLIENT_FIELD_MAP = { CLIENT_FULL_NAME: "full_name", CLIENT_ADDRESS: "address" };
  const saveClientField = async (prop, value, label) => {
    if (!prop || !app.client_id) return;
    if (String(ctx.client?.[prop] || "") === String(value || "")) return; // unchanged
    try {
      await api.put(`/clients/${app.client_id}`, { [prop]: value });
      toast.success(`Saved to client profile — ${label}`);
      load(); onAppChanged?.();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const saveToClient = (k, value) => saveClientField(CLIENT_FIELD_MAP[k], value, RF_LABELS[k] || k);

  const overrides = () => Object.fromEntries((ctx.editable || []).map((k) => [k, fields[k] ?? ""]));

  const requestDeletion = async (kind, targetId, label) => {
    try { await api.post("/deletion-requests", { kind, target_id: targetId, label, link: window.location.pathname + window.location.search }); toast.success("Deletion request sent to admins"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const restoreDeleted = async (token) => {
    if (!token) return;
    try { await api.post(`/deleted-items/${token}/restore`); toast.success("Restored"); load(); onAppChanged?.(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const deleteInvoice = async () => {
    if (!caseInvoice) return;
    if (needsApproval) return requestDeletion("invoice", caseInvoice.id, `${caseInvoice.number} (${app.app_number})`);
    if (!window.confirm(`Delete invoice ${caseInvoice.number} and all its payments?`)) return;
    try { const r = await api.delete(`/invoices/${caseInvoice.id}`); load(); onAppChanged?.(); toast.success("Invoice deleted", { duration: 10000, action: { label: "Undo", onClick: () => restoreDeleted(r.data.undo_token) } }); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const deletePayment = async (p) => {
    if (needsApproval) return requestDeletion("payment", p.id, `${p.receipt_number || money(p.amount, cur)} (${app.app_number})`);
    if (!window.confirm(`Void payment ${p.receipt_number || ""} of ${money(p.amount, cur)}? The invoice balance will be recalculated.`)) return;
    try { const r = await api.delete(`/payments/${p.id}`); load(); toast.success("Payment voided", { duration: 10000, action: { label: "Undo", onClick: () => restoreDeleted(r.data.undo_token) } }); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const deleteRetainer = async (v) => {
    if (needsApproval) return requestDeletion("retainer", v.id, `v${v.version} (${app.app_number})`);
    if (!window.confirm(`Delete retainer v${v.version}?${v.signature?.status === "Signed" ? " This also removes the signed copy filed on the case." : ""} This cannot be undone.`)) return;
    try { await api.delete(`/retainers/${v.id}`); toast.success(`Retainer v${v.version} deleted`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  // the retainer previews as a PDF, rendered the same way as the invoice/receipt PDFs
  const showPdf = (buf, label) => {
    setLegacyHtml(""); setPreviewLabel(label);
    setPdfUrl((prev) => { if (prev) window.URL.revokeObjectURL(prev); return window.URL.createObjectURL(new Blob([buf], { type: "application/pdf" })); });
  };
  const preview = async () => {
    setBusy("preview");
    try {
      const r = await api.post(`/applications/${appId}/retainer/preview-pdf`, { fields: overrides() }, { responseType: "arraybuffer" });
      showPdf(r.data, "Unsaved preview");
    } catch (e) { toast.error(await blobErr(e)); } finally { setBusy(""); }
  };
  const save = async () => {
    setBusy("save");
    try { const r = await api.post(`/applications/${appId}/retainer/generate`, { fields: overrides(), notes }); toast.success(`Retainer v${r.data.version} saved to case`); setNotes(""); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };
  const viewVersion = async (v) => {
    try {
      if (v.file_id) {
        const r = await api.get(`/retainers/${v.id}/pdf`, { responseType: "arraybuffer" });
        showPdf(r.data, `Saved v${v.version}`);
      } else {
        const r = await api.get(`/retainers/${v.id}`);
        setPdfUrl((prev) => { if (prev) window.URL.revokeObjectURL(prev); return ""; });
        setPreviewLabel(`Saved v${v.version} (legacy)`); setLegacyHtml(r.data.html || "");
      }
    } catch (e) { toast.error(await blobErr(e)); }
  };

  const sendForSignature = async (v) => {
    try {
      const r = await api.post(`/retainers/${v.id}/signature-request`);
      setSignLink({ version: v.version, url: r.data.url || `${window.location.origin}/sign/${r.data.token}`, expires: r.data.expires_at, emailedTo: r.data.emailed_to, emailError: r.data.email_error });
      if (r.data.emailed_to) toast.success(`Signing link emailed to ${r.data.emailed_to}`);
      else toast.warning(`Link created but not emailed: ${r.data.email_error || "unknown error"}. Copy it below.`);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const countersign = async (v) => {
    if (!window.confirm(`Add the RCIC signature to retainer v${v.version}? This creates the fully executed PDF.`)) return;
    try { await api.post(`/retainers/${v.id}/countersign`); toast.success("Countersigned — fully executed PDF saved to the case"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(signLink.url); toast.success("Link copied — paste it into WhatsApp or email"); }
    catch { toast.info("Select the link and copy it manually"); }
  };
  const removeDiscount = async () => {
    if (!window.confirm("Remove the discount from this case? Fees, HST and the invoice will recalculate.")) return;
    try { await api.put(`/applications/${appId}/fee-items`, { fee_discount: null }); toast.success("Discount removed"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const regenerateRetainer = async () => {
    setBusy("regen");
    try { const r = await api.post(`/applications/${appId}/retainer/generate`, { fields: overrides() }); toast.success(`Retainer v${r.data.version} regenerated to match the current fee`); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };

  const masterNames = master.filter((m) => m.professional_fee !== null && m.professional_fee !== undefined).map((m) => m.service);
  const inMaster = masterNames.includes(app.service_type);
  const canGenerate = fees.configured && ctx.template?.available;

  return (
    <div className="space-y-6">
      {showFees && (
      <div className="lg:max-w-2xl">
        {/* Fee summary — every number comes from GET /applications/:id/fees (master fee source + existing invoices/payments) */}
        <Card className="p-6 space-y-4" data-testid="case-fee-summary">
          <div className="flex items-center justify-between gap-2">
            <h3 className="font-head font-bold text-slate-900">Professional fee</h3>
            <span className="text-xs text-slate-400">Source: {fees.flat ? "Flat fee" : "Master Fee List"}</span>
          </div>
          <div>
            <label className="text-[11px] font-medium text-slate-500">Service / Application</label>
            <ServiceCombobox testid="case-fee-service" value={inMaster ? app.service_type : ""} options={masterNames}
              onSelect={(s) => changeService(s)}
              placeholder={app.service_type ? `${app.service_type} (not in master fee list) — select a service…` : "Select a service…"} />
          </div>
          {fees.configured && <TaxExemptToggle appId={appId} fees={fees} onSaved={load} />}
          {fees.retainer?.mismatch && (
            <div data-testid="retainer-mismatch-badge" className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800 flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold">{"Retainer amount doesn't match the current fee."}</div>
                <div className="text-[11px] text-amber-700">Latest retainer v{fees.retainer.version}: {money(fees.retainer.total, cur)} · current total: {money(fees.total, cur)}.</div>
              </div>
              <button data-testid="retainer-regenerate-fees" onClick={regenerateRetainer} disabled={!!busy} className="shrink-0 bg-amber-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold hover:brightness-110 disabled:opacity-60">{busy === "regen" ? "Regenerating…" : "Regenerate retainer"}</button>
            </div>
          )}
          {!fees.configured ? (
            <div data-testid="case-fee-not-configured" className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800 flex gap-2">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /><span>{fees.message} No fee has been assumed.</span>
            </div>
          ) : fees.flat ? (
            <dl className="text-sm divide-y divide-slate-100" data-testid="case-fee-flat">
              <FeeRow testid="fee-flat-amount" label={`Flat fee — ${fees.service}`} value={money((fees.lines || [])[0]?.total ?? fees.professional_fee, cur)} strong />
              {(fees.lines || []).filter((l) => l.kind === "case_adjustment").map((l) => (
                <FeeRow key={l.id} testid={`fee-adjustment-${l.id}`} label={l.label} value={money(l.total, cur)}
                  onRemove={hasCaseEdit ? () => removeFeeAdjustment(appId, l, load) : undefined} removeTestid={`fee-adjustment-remove-${l.id}`} removeTitle="Remove adjustment" />
              ))}
              {(fees.lines || []).some((l) => l.kind === "case_adjustment") && <FeeRow testid="fee-flat-professional" label="Professional fee" value={money(fees.professional_fee, cur)} strong />}
              <FeeRow testid="fee-flat-hst" label={fees.tax_exempt ? "Tax exempt (no HST)" : `HST (${fees.hst_rate}%)`} value={money(fees.hst, cur)} />
              <FeeRow testid="fee-flat-total" label="Total" value={money(fees.total, cur)} strong />
              <FeeRow testid="fee-paid" label="Payments received" value={money(fees.paid, cur)} className="text-emerald-600" />
              <FeeRow testid="fee-balance" label="Remaining balance" value={money(fees.balance, cur)} strong className="text-rose-600" />
            </dl>
          ) : (
            <dl className="text-sm divide-y divide-slate-100">
              {(fees.lines || []).map((l, i) => (
                <FeeRow key={i} testid={l.kind === "case_adjustment" ? `fee-adjustment-${l.id}` : `fee-line-${i}`} label={l.kind === "base" ? `Base fee — ${l.label}` : (l.kind === "adjustment" || l.kind === "case_adjustment") ? l.label : l.kind === "discount" ? `Discount: ${l.label}` : `${l.kind === "addon" ? "Add-on" : "Additional fee"}: ${l.label}${l.qty > 1 ? ` × ${l.qty}` : ""}`}
                  value={money(l.total, cur)}
                  onRemove={l.kind === "discount" ? removeDiscount : (l.kind === "case_adjustment" && hasCaseEdit) ? () => removeFeeAdjustment(appId, l, load) : undefined}
                  removeTestid={l.kind === "case_adjustment" ? `fee-adjustment-remove-${l.id}` : "fee-discount-remove"} removeTitle={l.kind === "case_adjustment" ? "Remove adjustment" : "Remove discount"} />
              ))}
              <FeeRow testid="fee-professional" label="Professional fee" value={money(fees.professional_fee, cur)} strong />
              <FeeRow testid="fee-hst" label={fees.tax_exempt ? "Tax exempt (no HST)" : `HST (${fees.hst_rate}%)`} value={money(fees.hst, cur)} />
              <FeeRow testid="fee-total" label="Total professional fee" value={money(fees.total, cur)} strong />
              <FeeRow testid="fee-paid" label="Payments received" value={money(fees.paid, cur)} className="text-emerald-600" />
              <FeeRow testid="fee-balance" label="Remaining balance" value={money(fees.balance, cur)} strong className="text-rose-600" />
              {fees.notes && <div className="pt-2 text-xs text-slate-500" data-testid="fee-notes">Rate list note: {fees.notes}</div>}
            </dl>
          )}
          {fees.configured && hasCaseEdit && <FeeAdjustmentForm appId={appId} onSaved={load} />}
          {fees.configured && !fees.flat && <FeeItemsEditor appId={appId} fees={fees} onSaved={load} />}
          {fees.configured && (
            <div className="flex flex-wrap gap-2 items-center">
              {caseInvoice ? (
                <>
                  <span className="text-xs text-slate-500" data-testid="case-invoice-number">Invoice {caseInvoice.number}</span>
                  <StatusBadge status={caseInvoice.status} />
                  <div className="ml-auto flex items-center gap-2 flex-wrap">
                    <button data-testid="case-invoice-preview" onClick={() => setInvoicePreview(true)} className="inline-flex items-center gap-1.5 border border-slate-200 text-slate-600 px-3 py-2 rounded-lg text-xs font-semibold hover:bg-slate-50"><Eye className="h-3.5 w-3.5" /> Preview</button>
                    <button data-testid="case-record-payment" onClick={() => setPayOpen((v) => !v)} className="inline-flex items-center gap-1.5 bg-secondary text-white px-3 py-2 rounded-lg text-xs font-semibold hover:brightness-110 transition-[filter]"><DollarSign className="h-3.5 w-3.5" /> {payOpen ? "Close payment form" : "Record payment / deposit"}</button>
                    <button data-testid="case-delete-invoice" onClick={deleteInvoice} title={needsApproval ? "Request deletion from an admin" : "Delete invoice"} className="inline-flex items-center gap-1.5 border border-rose-200 text-rose-600 px-3 py-2 rounded-lg text-xs font-semibold hover:bg-rose-50"><Trash2 className="h-3.5 w-3.5" /> {needsApproval ? "Request delete" : "Delete"}</button>
                  </div>
                </>
              ) : (
                <button data-testid="case-create-invoice" onClick={createInvoice} disabled={busy === "invoice"} className="inline-flex items-center gap-1.5 bg-secondary text-white px-3 py-2 rounded-lg text-xs font-semibold disabled:opacity-60"><Receipt className="h-3.5 w-3.5" /> {busy === "invoice" ? "Creating…" : (fees.flat ? "Create invoice from flat fee" : "Create invoice from master fee")}</button>
              )}
            </div>
          )}
          {fees.configured && fees.balance > 0 && (
            <div className="mt-3 flex items-center gap-2 flex-wrap rounded-xl border border-primary/15 bg-primary/5 px-3 py-2.5" data-testid="case-paylink-row">
              <span className="text-xs font-semibold text-slate-700">Online payment</span>
              <span className="text-[11px] text-slate-500">Client pays the {money(fees.balance, cur)} balance by card or PayPal</span>
              <div className="ml-auto"><PayLinkButtons applicationId={app.id} testid="case-paylink" /></div>
            </div>
          )}
          {fees.configured && <div className="mt-3"><PaymentPlanCard key={`${(fees.payments || []).length}-${fees.paid}`} appId={app.id} canEdit={user?.role === "admin" || (user?.permissions || []).includes("edit_cases")} onChanged={load} /></div>}
          {payOpen && caseInvoice && (
            <PaymentRecorder invoice={caseInvoice} clientId={app.client_id} fees={fees} onSaved={() => { setPayOpen(false); load(); onAppChanged?.(); }} />
          )}
          {(fees.payments || []).length > 0 && (
            <div className="border-t border-slate-100 pt-3 space-y-1.5" data-testid="case-payments">
              <div className="flex items-center justify-between">
                <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Payment log ({fees.payments.length})</div>
                <div className="text-[11px] text-slate-500">Paid <b className="text-emerald-600" data-testid="case-payments-total">{money(fees.paid, cur)}</b> of {money(fees.total, cur)}</div>
              </div>
              {fees.payments.map((p) => (
                <div key={p.id} className="text-xs bg-slate-50 rounded-lg px-3 py-2 space-y-0.5" data-testid={`case-payment-${p.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-slate-500 min-w-0 truncate">{p.receipt_number || "—"} · {p.method} · {p.paid_on || (p.created_at || "").slice(0, 10)}</span>
                    <span className="flex items-center gap-2 shrink-0"><PaymentStateTag payment={p} /><b className={(p.status === "Pending approval" || p.status === "Rejected") ? "text-slate-400 line-through decoration-slate-300" : "text-emerald-600"} data-testid={`case-payment-amount-${p.id}`}>{money(p.amount, cur)}</b>{(!p.status || p.status === "Completed") && <PaymentReceiptActions payment={p} onEmailed={load} />}<button data-testid={`case-payment-delete-${p.id}`} onClick={() => deletePayment(p)} title={needsApproval ? "Request deletion from an admin" : "Void payment"} className="text-rose-500 hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button></span>
                  </div>
                  <div className="text-[11px] text-slate-400" data-testid={`case-payment-meta-${p.id}`}>
                    {p.balance_after !== null && p.balance_after !== undefined ? <>Balance after: <b className={p.balance_after > 0 ? "text-rose-500" : "text-emerald-600"}>{money(p.balance_after, cur)}</b> · </> : null}
                    Recorded by {p.recorded_by || "—"}{p.note ? ` · ${p.note}` : ""}
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="text-xs text-slate-500 grid grid-cols-2 gap-2 pt-1 border-t border-slate-100">
            <div><span className="text-slate-400">Client phone:</span> <span data-testid="fee-client-phone">{ctx.client?.phone || "—"}</span></div>
            <div><span className="text-slate-400">Client email:</span> <span data-testid="fee-client-email">{ctx.client?.email || "—"}</span></div>
          </div>
        </Card>
      </div>
      )}

      {showRetainer && (
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="space-y-6">
        <Card className="p-6 space-y-4">
          <div className="flex items-center justify-between gap-2">
            <h3 className="font-head font-bold text-slate-900">Retainer details</h3>
          </div>
          {ctx.missing.length > 0 && (
            <div data-testid="retainer-missing" className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800">
              Missing information: <b>{ctx.missing.map((m) => RF_LABELS[m] || m).join(", ")}</b>. Fill it in below before generating.
            </div>
          )}
          <p className="text-[11px] text-slate-400">{"Values are pulled from the case. The client's name and address save back to the client profile; the fee values change only this retainer document."}</p>
          {fees.tax_exempt && (
            <div data-testid="retainer-tax-exempt-note" className="text-xs rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 px-3 py-2">
              This case is <b>tax exempt</b> — the retainer prints “(Tax exempt — no HST)” next to the fee instead of an HST rate.
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            {ctx.merge_fields.map((k) => {
              const editable = (ctx.editable || []).includes(k);
              if (k === "PROFESSIONAL_FEE" && fees.configured && !fees.flat) return (
                <ProfessionalFeeField key={k} appId={appId} fees={fees} label={RF_LABELS[k]} onSaved={load} />
              );
              if (k === "DEPOSIT_AMOUNT") return <RetainerDepositField key={k} appId={appId} label={RF_LABELS[k]} deposit={ctx.deposit} onSaved={load} />;
              return (
                <div key={k} className={k === "CLIENT_ADDRESS" || k === "SERVICE_NAME" ? "col-span-2" : ""}>
                  <label className="text-[11px] font-medium text-slate-500">{RF_LABELS[k] || k}{!editable && <span className="text-slate-300"> · auto</span>}{CLIENT_FIELD_MAP[k] && <span className="text-slate-300"> · saves to client</span>}</label>
                  <input data-testid={`rf-${k}`} value={fields[k] ?? ""} readOnly={!editable} type={k === "RETAINER_DATE" ? "date" : "text"}
                    onChange={(e) => setFields({ ...fields, [k]: e.target.value })}
                    onBlur={(e) => CLIENT_FIELD_MAP[k] && editable && saveToClient(k, e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && CLIENT_FIELD_MAP[k] && editable) e.currentTarget.blur(); }}
                    className={`mt-1 w-full px-2.5 py-2 rounded-lg border border-slate-200 text-sm ${editable ? "" : "bg-slate-50 text-slate-600"}`} />
                  {CLIENT_FIELD_MAP[k] && <LastChangedHint entry={fieldHist[CLIENT_FIELD_MAP[k]]} />}
                </div>
              );
            })}
          </div>
          <ClientContactFields client={ctx.client || {}} hist={fieldHist} onSave={saveClientField} />
          {fees.configured && <DepositRecorder appId={appId} clientId={app.client_id} fees={fees} caseInvoice={caseInvoice} onSaved={load} />}
          <div>
            <label className="text-[11px] font-medium text-slate-500">Notes (kept with this retainer in the CRM — not printed in the agreement)</label>
            <textarea data-testid="retainer-notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. Deposit paid by e-transfer; balance due before submission"
              className="mt-1 w-full px-2.5 py-2 rounded-lg border border-slate-200 text-sm" />
          </div>
          <div className="flex gap-2">
            <button data-testid="retainer-preview-btn" onClick={preview} disabled={!canGenerate || !!busy} className="flex-1 border border-slate-200 rounded-xl py-2.5 text-sm font-semibold inline-flex items-center justify-center gap-2 hover:bg-slate-50 disabled:opacity-50"><Eye className="h-4 w-4" /> {busy === "preview" ? "Generating…" : "Preview retainer"}</button>
            <button data-testid="retainer-generate-save" onClick={save} disabled={!canGenerate || !!busy} className="flex-1 bg-primary text-white rounded-xl py-2.5 text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50"><Save className="h-4 w-4" /> {busy === "save" ? "Saving…" : "Save retainer to case"}</button>
          </div>
        </Card>
      </div>

      <div className="space-y-4">
        <Card className="p-3">
          <div className="flex items-center justify-between gap-2 px-1 pb-2">
            <div className="text-xs font-semibold text-slate-500">Preview {previewLabel && <span className="text-slate-400">· {previewLabel}</span>}</div>
            {pdfUrl && (
              <button data-testid="retainer-preview-open" onClick={() => window.open(pdfUrl, "_blank")}
                className="text-[11px] font-semibold text-slate-500 hover:text-primary inline-flex items-center gap-1 transition-colors"><Eye className="h-3.5 w-3.5" /> Open full size</button>
            )}
          </div>
          <div className="h-[560px] overflow-auto bg-slate-200 rounded-lg border border-slate-100 p-1" data-testid="retainer-preview">
            {!previewLabel && <div className="h-full flex items-center justify-center text-sm text-slate-400 p-6 text-center"><FileSignature className="h-4 w-4 mr-2" /> {"Click \"Preview retainer\" to render the agreement as a PDF with this case's details."}</div>}
            {legacyHtml && <iframe title="retainer-preview-legacy" srcDoc={legacyHtml} className="w-full h-full bg-white rounded" />}
            {pdfUrl && <iframe title="retainer-preview-pdf" src={pdfUrl} data-testid="retainer-preview-frame" className="w-full h-full bg-white rounded" />}
          </div>
        </Card>
        <Card className="p-4">
          <h4 className="font-head font-bold text-slate-900 mb-2">Saved retainers ({versions.length})</h4>
          {fees.retainer?.mismatch && (
            <div data-testid="retainer-mismatch-badge-list" className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800 flex items-start gap-2 mb-3">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold">{"Retainer amount doesn't match the current fee."}</div>
                <div className="text-[11px] text-amber-700">Latest v{fees.retainer.version}: {money(fees.retainer.total, cur)} · current: {money(fees.total, cur)}.</div>
              </div>
              <button data-testid="retainer-regenerate-list" onClick={regenerateRetainer} disabled={!!busy} className="shrink-0 bg-amber-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold hover:brightness-110 disabled:opacity-60">{busy === "regen" ? "Regenerating…" : "Regenerate retainer"}</button>
            </div>
          )}
          {versions.length === 0 ? <p className="text-sm text-slate-400">No retainers saved yet.</p> : (
            <div className="space-y-2">
              {versions.map((v) => {
                const signed = v.signature?.status === "Signed";
                const pending = !signed && v.signature_request?.status === "Pending";
                return (
                <div key={v.id} data-testid={`retainer-version-${v.version}`} className="bg-slate-50 rounded-lg px-3 py-2 text-sm space-y-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0"><b>v{v.version}</b> · <span className="text-slate-600">{v.file_id ? "Master Word retainer" : v.template_name}</span> <span className="text-xs text-slate-400">· {(v.created_at || "").slice(0, 10)} by {v.created_by}</span></div>
                    {v.file_id && <span data-testid={`retainer-sign-status-${v.version}`} className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${signed ? "bg-emerald-100 text-emerald-700" : pending ? "bg-amber-100 text-amber-700" : v.signature_request?.status === "Locked" ? "bg-rose-100 text-rose-700" : "bg-slate-200 text-slate-600"}`}>{signed ? (v.signature?.countersign ? "Fully executed" : "Signed by client") : pending ? "Awaiting signature" : v.signature_request?.status === "Locked" ? "Link locked" : "Not sent"}</span>}
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <button data-testid={`retainer-view-${v.version}`} onClick={() => viewVersion(v)} className="text-primary font-semibold text-xs inline-flex items-center gap-1"><Eye className="h-3.5 w-3.5" /> Preview</button>
                    <button data-testid={`retainer-download-${v.version}`} onClick={() => downloadFile(`/retainers/${v.id}/download`, v.file_name || `retainer-v${v.version}.html`)} className="text-slate-600 font-semibold text-xs inline-flex items-center gap-1"><Download className="h-3.5 w-3.5" /> Word</button>
                    {signed && !v.signature?.countersign && canCountersign && <button data-testid={`retainer-countersign-${v.version}`} onClick={() => countersign(v)} className="text-primary font-semibold text-xs inline-flex items-center gap-1"><FileSignature className="h-3.5 w-3.5" /> Countersign as RCIC</button>}
                    {signed && <button data-testid={`retainer-signed-pdf-${v.version}`} onClick={() => downloadFile(`/retainers/${v.id}/signed-pdf`, v.signature.pdf_file_name || "signed-retainer.pdf")} className="text-emerald-700 font-semibold text-xs inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> {v.signature?.countersign ? "Executed PDF" : "Signed PDF"}</button>}
                    {v.file_id && !signed && <button data-testid={`retainer-send-sign-${v.version}`} onClick={() => sendForSignature(v)} className="text-secondary font-semibold text-xs inline-flex items-center gap-1"><Send className="h-3.5 w-3.5" /> {pending ? "New signing link" : "Send for signature"}</button>}
                    <button data-testid={`retainer-delete-${v.version}`} onClick={() => deleteRetainer(v)} title={needsApproval ? "Request deletion from an admin" : "Delete this retainer"} className="text-rose-500 hover:text-rose-600 font-semibold text-xs inline-flex items-center gap-1 ml-auto"><Trash2 className="h-3.5 w-3.5" /> {needsApproval ? "Request delete" : "Delete"}</button>
                  </div>
                  {pending && v.signature_request?.emailed_to && signLink?.version !== v.version && <div className="text-[11px] text-slate-500">Signing link emailed to {v.signature_request.emailed_to} · {(v.signature_request.emailed_at || "").slice(0, 10)}</div>}
                  {v.notes && <div className="text-xs text-slate-600 whitespace-pre-wrap bg-white rounded px-2 py-1 border border-slate-100" data-testid={`retainer-notes-${v.version}`}>{v.notes}</div>}
                  {signed && <div className="text-[11px] text-slate-500" data-testid={`retainer-signed-info-${v.version}`}>Signed by {v.signature.typed_name}{v.signature.initials ? ` (initials ${v.signature.initials})` : ""} · {(v.signature.signed_at || "").slice(0, 16).replace("T", " ")} UTC · IP {v.signature.ip || "—"}{v.signature.countersign ? ` · Countersigned by ${v.signature.countersign.name} ${(v.signature.countersign.signed_at || "").slice(0, 10)}` : " · Awaiting RCIC countersignature"}</div>}
                  {signLink?.version === v.version && !signed && (
                    <div className="bg-white border border-slate-200 rounded-lg p-2 space-y-1" data-testid={`retainer-sign-link-box-${v.version}`}>
                      <div className="flex gap-2">
                        <input readOnly value={signLink.url} data-testid="retainer-sign-link" onFocus={(e) => e.target.select()} className="flex-1 min-w-0 px-2 py-1.5 rounded border border-slate-200 text-xs font-mono" />
                        <button data-testid="retainer-sign-link-copy" onClick={copyLink} className="px-2.5 py-1.5 rounded bg-secondary text-white text-xs font-semibold inline-flex items-center gap-1"><Copy className="h-3.5 w-3.5" /> Copy</button>
                      </div>
                      {signLink.emailedTo ? <p className="text-[11px] text-emerald-600" data-testid="retainer-sign-emailed">Emailed to {signLink.emailedTo}</p> : <p className="text-[11px] text-amber-600" data-testid="retainer-sign-email-error">Not emailed: {signLink.emailError}</p>}
                      <p className="text-[11px] text-slate-400">One-time link, valid until {(signLink.expires || "").slice(0, 10)}. Creating a new link cancels this one.</p>
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>
      </div>
      )}

      {invoicePreview && caseInvoice && (
        <InvoicePreviewModal invoice={caseInvoice} onClose={() => setInvoicePreview(false)} />
      )}
    </div>
  );
}

const FeeRow = ({ label, value, strong, className = "", testid, onRemove, removeTestid, removeTitle }) => (
  <div className="group flex justify-between gap-3 py-2">
    <dt className="text-slate-500">{label}</dt>
    <dd className="flex items-center gap-2">
      <span data-testid={testid} className={`text-right ${strong ? "font-bold" : "font-medium"} ${className || (strong ? "text-slate-900" : "text-slate-700")}`}>{value}</span>
      {onRemove && (
        <button data-testid={removeTestid} onClick={onRemove} title={removeTitle || "Remove"}
          className="text-slate-300 hover:text-rose-500 md:opacity-0 md:group-hover:opacity-100 transition-opacity"><Trash2 className="h-3.5 w-3.5" /></button>
      )}
    </dd>
  </div>
);

// "Tax exempt (no HST)" checkbox — works for ANY case (flat or master-list). A flat/custom fee starts
// tax exempt; it can be unticked here to add HST on top. PUT /applications/:id/fee-items { tax_exempt }.
function TaxExemptToggle({ appId, fees, onSaved }) {
  const [saving, setSaving] = useState(false);
  const on = !!fees.tax_exempt;
  const toggle = async () => {
    setSaving(true);
    try {
      await api.put(`/applications/${appId}/fee-items`, { tax_exempt: !on });
      toast.success(!on ? "Marked tax exempt — no HST" : "HST will be added to this case");
      onSaved?.();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };
  return (
    <label className={`flex items-start gap-2.5 rounded-xl border p-3 cursor-pointer select-none ${on ? "border-emerald-200 bg-emerald-50/60" : "border-slate-200"}`} data-testid="tax-exempt-control">
      <input type="checkbox" data-testid="tax-exempt-checkbox" checked={on} disabled={saving} onChange={toggle} className="mt-0.5 h-4 w-4 accent-emerald-600" />
      <span className="min-w-0">
        <span className="block text-sm font-semibold text-slate-800">Tax exempt (no HST)</span>
        <span className="block text-[11px] text-slate-500">
          {fees.flat ? "Flat fees are tax exempt by default. Untick to add HST on top." : "Tick to remove HST from this case's fee."} Shown as “Tax exempt” on the retainer, invoice and receipts.
        </span>
      </span>
    </label>
  );
}



function InvoicePreviewModal({ invoice, onClose }) {
  const [url, setUrl] = useState("");
  const [err, setErr] = useState("");
  useEffect(() => {
    let blobUrl;
    api.get(`/invoices/${invoice.id}/pdf`, { responseType: "blob" })
      .then((r) => { blobUrl = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" })); setUrl(blobUrl); })
      .catch((e) => setErr(apiErr(e)));
    return () => { if (blobUrl) window.URL.revokeObjectURL(blobUrl); };
  }, [invoice.id]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" data-testid="invoice-preview-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl h-[88vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <div className="font-head font-bold text-slate-900" data-testid="invoice-preview-number">Invoice {invoice.number}</div>
            <div className="text-xs text-slate-400">{invoice.service || ""}</div>
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge status={invoice.status} />
            <button data-testid="invoice-preview-download" onClick={() => downloadFile(`/invoices/${invoice.id}/pdf`, `Invoice-${invoice.number}.pdf`)}
              className="inline-flex items-center gap-1.5 border border-slate-200 text-slate-600 px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-slate-50 transition-colors">
              <Download className="h-3.5 w-3.5" /> Download PDF
            </button>
            <button data-testid="invoice-preview-close" onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors"><X className="h-5 w-5" /></button>
          </div>
        </div>
        <div className="flex-1 bg-slate-200 p-2 overflow-hidden">
          {err ? (
            <div className="h-full flex items-center justify-center text-sm text-rose-600" data-testid="invoice-preview-error">{err}</div>
          ) : url ? (
            <iframe title="Invoice PDF" src={url} data-testid="invoice-preview-frame" className="w-full h-full rounded-lg bg-white" />
          ) : (
            <div className="h-full flex items-center justify-center text-sm text-slate-500" data-testid="invoice-preview-loading">Rendering invoice PDF…</div>
          )}
        </div>
      </div>
    </div>
  );
}

function FeeItemsEditor({ appId, fees, onSaved }) {
  const initQty = () => Object.fromEntries((fees.available_addons || []).map((a) => [a.label, (fees.fee_addons || []).find((x) => x.label === a.label)?.qty || 0]));
  const [qty, setQty] = useState(initQty);
  const [extras, setExtras] = useState(() => (fees.extra_fees || []).map((x) => ({ ...x })));
  const initDisc = () => ({ type: fees.fee_discount?.type || "amount", value: fees.fee_discount?.value ?? "", label: fees.fee_discount?.label || "" });
  const [disc, setDisc] = useState(initDisc);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setQty(initQty()); setExtras((fees.extra_fees || []).map((x) => ({ ...x }))); setDisc(initDisc()); }, [JSON.stringify(fees.fee_addons), JSON.stringify(fees.extra_fees), JSON.stringify(fees.fee_discount), fees.service]);
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/applications/${appId}/fee-items`, {
        fee_addons: Object.entries(qty).filter(([, q]) => q > 0).map(([label, q]) => ({ label, qty: q })),
        extra_fees: extras.map((x) => ({ id: x.id, label: x.label, amount: parseFloat(x.amount) })),
        fee_discount: disc.value === "" || Number(disc.value) === 0 ? null : { type: disc.type, value: parseFloat(disc.value), label: disc.label || "Discount" },
      });
      toast.success("Fees updated"); onSaved();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };
  const clearDiscount = async () => {
    setDisc({ type: "amount", value: "", label: "" });
    if (!fees.fee_discount) return; // nothing saved yet — just clearing the inputs
    setSaving(true);
    try { await api.put(`/applications/${appId}/fee-items`, { fee_discount: null }); toast.success("Discount deleted"); onSaved(); }
    catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };
  const setExtra = (i, patch) => setExtras(extras.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  return (
    <div className="border-t border-slate-100 pt-3 space-y-3" data-testid="fee-items-editor">
      {(fees.available_addons || []).length > 0 && (
        <div className="space-y-2">
          <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Add-ons from master list</div>
          {fees.available_addons.map((a, i) => (
            <div key={a.label} className="flex items-center justify-between gap-2 text-sm" data-testid={`addon-row-${i}`}>
              <span className="text-slate-600">{a.label} <span className="text-slate-400">(+${Number(a.amount).toFixed(2)} each)</span></span>
              <div className="flex items-center gap-1.5">
                <button data-testid={`addon-minus-${i}`} onClick={() => setQty({ ...qty, [a.label]: Math.max(0, (qty[a.label] || 0) - 1) })} className="h-7 w-7 rounded-lg border border-slate-200 inline-flex items-center justify-center"><Minus className="h-3.5 w-3.5" /></button>
                <span data-testid={`addon-qty-${i}`} className="w-6 text-center font-semibold">{qty[a.label] || 0}</span>
                <button data-testid={`addon-plus-${i}`} onClick={() => setQty({ ...qty, [a.label]: Math.min(20, (qty[a.label] || 0) + 1) })} className="h-7 w-7 rounded-lg border border-slate-200 inline-flex items-center justify-center"><Plus className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="space-y-2">
        <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Additional fees (this case only)</div>
        {extras.map((x, i) => (
          <div key={x.id || i} className="flex gap-2" data-testid={`extra-fee-row-${i}`}>
            <input data-testid={`extra-fee-label-${i}`} placeholder="Description" value={x.label} onChange={(e) => setExtra(i, { label: e.target.value })} className="flex-1 min-w-0 px-2.5 py-1.5 rounded-lg border border-slate-200 text-sm" />
            <input data-testid={`extra-fee-amount-${i}`} type="number" min="0" step="0.01" placeholder="Amount" value={x.amount} onChange={(e) => setExtra(i, { amount: e.target.value })} className="w-28 px-2.5 py-1.5 rounded-lg border border-slate-200 text-sm text-right" />
            <button data-testid={`extra-fee-remove-${i}`} onClick={() => setExtras(extras.filter((_, j) => j !== i))} className="text-rose-500 px-1"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
        <button data-testid="extra-fee-add" onClick={() => setExtras([...extras, { label: "", amount: "" }])} className="text-primary text-xs font-semibold inline-flex items-center gap-1"><Plus className="h-3.5 w-3.5" /> Add additional fee</button>
      </div>
      <div className="space-y-2">
        <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Discount</div>
        <div className="flex gap-2" data-testid="discount-row">
          <input data-testid="discount-label" placeholder="Reason (optional)" value={disc.label} onChange={(e) => setDisc({ ...disc, label: e.target.value })} className="flex-1 min-w-0 px-2.5 py-1.5 rounded-lg border border-slate-200 text-sm" />
          <input data-testid="discount-value" type="number" min="0" step="0.01" placeholder="0" value={disc.value} onChange={(e) => setDisc({ ...disc, value: e.target.value })} className="w-24 px-2.5 py-1.5 rounded-lg border border-slate-200 text-sm text-right" />
          <select data-testid="discount-type" value={disc.type} onChange={(e) => setDisc({ ...disc, type: e.target.value })} className="px-2 py-1.5 rounded-lg border border-slate-200 text-sm">
            <option value="amount">$</option>
            <option value="percent">%</option>
          </select>
          {(disc.value !== "" || fees.fee_discount) && <button data-testid="discount-clear" title="Delete discount" onClick={clearDiscount} disabled={saving} className="text-rose-500 px-1 disabled:opacity-50"><Trash2 className="h-4 w-4" /></button>}
        </div>
      </div>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] text-slate-400">Discount is taken off before HST. The case invoice updates automatically.</span>
        <button data-testid="fee-items-save" onClick={save} disabled={saving} className="shrink-0 bg-primary text-white px-3 py-1.5 rounded-lg text-xs font-semibold disabled:opacity-60">{saving ? "Saving…" : "Save fees"}</button>
      </div>
    </div>
  );
}

// Staff/admin can set this case's professional fee; it flows to HST, total, balance, admin fee, invoice and retainer.
function ProfessionalFeeField({ appId, fees, label, onSaved }) {
  const gross = fees.gross_professional_fee ?? fees.professional_fee;
  const [val, setVal] = useState(String(gross ?? ""));
  const [saving, setSaving] = useState(false);
  useEffect(() => { setVal(String(gross ?? "")); }, [gross]);
  const dirty = Number(val) !== Number(gross);
  const put = async (fee_override, msg) => {
    setSaving(true);
    try { await api.put(`/applications/${appId}/fee-items`, { fee_override }); toast.success(msg); onSaved(); }
    catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };
  const save = () => {
    const n = parseFloat(val);
    if (!(n >= 0)) { toast.error("Enter a valid professional fee"); return; }
    put(n === Number(fees.master_professional_fee) ? null : n, "Professional fee updated for this case");
  };
  return (
    <div className="col-span-2" data-testid="rf-professional-fee-field">
      <label className="text-[11px] font-medium text-slate-500">{label} <span className="text-slate-300">· editable</span></label>
      <div className="mt-1 flex gap-2">
        <input data-testid="rf-PROFESSIONAL_FEE" type="number" min="0" step="0.01" value={val} onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && dirty) save(); }}
          className="flex-1 min-w-0 px-2.5 py-2 rounded-lg border border-slate-200 text-sm" />
        <button data-testid="rf-professional-fee-save" onClick={save} disabled={!dirty || saving} className="px-3 py-2 rounded-lg bg-secondary text-white text-xs font-semibold disabled:opacity-40">{saving ? "Saving…" : "Save fee"}</button>
      </div>
      <div className="mt-1 text-[11px] text-slate-400 flex flex-wrap gap-x-2">
        <span>Master list amount: ${Number(fees.master_professional_fee ?? 0).toFixed(2)}</span>
        {fees.discount_amount > 0 && <span data-testid="rf-professional-fee-net">· after discount: ${Number(fees.professional_fee).toFixed(2)} (used in retainer)</span>}
        {fees.fee_override !== null && fees.fee_override !== undefined && (
          <button data-testid="rf-professional-fee-reset" onClick={() => put(null, "Reset to master list fee")} disabled={saving} className="text-primary font-semibold">Reset to master fee</button>
        )}
      </div>
    </div>
  );
}

// Phone / email / date of birth edited right here — each one saves onto the client profile on blur.
const CONTACT_FIELDS = [
  { prop: "phone", label: "Client phone", type: "tel" },
  { prop: "email", label: "Client email", type: "email" },
  { prop: "dob", label: "Date of birth", type: "date" },
];
function ClientContactFields({ client, hist, onSave }) {
  const [vals, setVals] = useState({});
  useEffect(() => { setVals({ phone: client.phone || "", email: client.email || "", dob: (client.dob || "").slice(0, 10) }); },
    [client.phone, client.email, client.dob]);
  const dupes = useDuplicateCheck({ email: vals.email, phone: vals.phone, excludeId: client.id });
  return (
    <div className="border-t border-slate-100 pt-3" data-testid="client-contact-fields">
      <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Client contact · saves to client</div>
      <div className="mt-2 grid grid-cols-3 gap-3">
        {CONTACT_FIELDS.map((f) => (
          <div key={f.prop}>
            <label className="text-[11px] font-medium text-slate-500">{f.label}</label>
            <input data-testid={`client-field-${f.prop}`} type={f.type} value={vals[f.prop] ?? ""}
              onChange={(e) => setVals({ ...vals, [f.prop]: e.target.value })}
              onBlur={(e) => onSave(f.prop, e.target.value, f.label)}
              onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
              className="mt-1 w-full px-2.5 py-2 rounded-lg border border-slate-200 text-sm focus:ring-2 focus:ring-primary/30" />
            <LastChangedHint entry={hist[f.prop]} />
          </div>
        ))}
      </div>
      <div className="mt-2"><DuplicateWarning matches={dupes} testId="retainer-duplicate-warning" /></div>
    </div>
  );
}

// Staff-facing recorder for full or partial payments. Every payment lands in the case payment log and
// flows straight through to the invoice status, the fee balance and the retainer's deposit/balance fields.
function PaymentRecorder({ invoice, clientId, fees, onSaved }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [proof, setProof] = useState(emptyProof());
  const cur = fees.currency || "CAD";
  const outstanding = Math.max(round2((invoice.total || 0) - (invoice.amount_paid || 0)), 0);
  const [amount, setAmount] = useState(outstanding ? String(outstanding) : "");
  const [method, setMethod] = useState("E-transfer");
  const [paidOn, setPaidOn] = useState(new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const n = parseFloat(amount);
    if (!(n > 0)) { toast.error("Enter the amount received"); return; }
    const problem = proofProblem(proof, isAdmin);
    if (problem) { toast.error(problem); return; }
    if (n > outstanding + 0.001 && !window.confirm(`That is more than the outstanding ${cur} ${outstanding.toFixed(2)}. Record anyway?`)) return;
    setSaving(true);
    try {
      const data = await submitPayment({ invoiceId: invoice.id, clientId, body: { amount: n, method, paid_on: paidOn, note }, proof });
      if (!data.pending_approval) toast.success(n < outstanding - 0.001 ? "Partial payment recorded — balance updated everywhere" : "Payment recorded — invoice settled");
      setProof(emptyProof());
      onSaved();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  return (
    <div className="border border-slate-200 rounded-xl p-3 space-y-2 bg-slate-50/70" data-testid="payment-recorder">
      <div className="flex items-center justify-between text-[11px]">
        <span className="font-semibold text-slate-500 uppercase tracking-wide">Record a payment</span>
        <span className="text-slate-500">Outstanding: <b className="text-rose-600" data-testid="payment-outstanding">{money(outstanding, cur)}</b></span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <input data-testid="payment-amount" type="number" min="0" step="0.01" placeholder="Amount received" value={amount} onChange={(e) => setAmount(e.target.value)}
          className="px-2.5 py-2 rounded-lg border border-slate-200 text-sm focus:ring-2 focus:ring-primary/30" />
        <select data-testid="payment-method" value={method} onChange={(e) => setMethod(e.target.value)} className="px-2 py-2 rounded-lg border border-slate-200 text-sm">
          {PAY_METHODS.map((m) => <option key={m}>{m}</option>)}
        </select>
        <input data-testid="payment-date" type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} className="px-2.5 py-2 rounded-lg border border-slate-200 text-sm" />
        <input data-testid="payment-note" placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} className="px-2.5 py-2 rounded-lg border border-slate-200 text-sm" />
      </div>
      <PaymentProofPicker value={proof} onChange={setProof} isAdmin={isAdmin} testPrefix="payment" />
      <div className="flex items-center gap-2">
        <button data-testid="payment-submit" onClick={submit} disabled={saving}
          className="inline-flex items-center gap-1.5 bg-secondary text-white px-3 py-2 rounded-lg text-xs font-semibold disabled:opacity-50 hover:brightness-110 transition-[filter]"><DollarSign className="h-3.5 w-3.5" /> {saving ? "Recording…" : (!proof.file && !isAdmin ? "Send for approval" : "Record payment")}</button>
        {amount && parseFloat(amount) > 0 && parseFloat(amount) < outstanding - 0.001 && (
          <span className="text-[11px] text-slate-500" data-testid="payment-remaining-hint">Remaining after this: {money(round2(outstanding - parseFloat(amount)), cur)}</span>
        )}
      </div>
    </div>
  );
}

// Records a deposit through the existing invoice/payment system (creates the case invoice from the master fee if needed).
const PAY_METHODS = ["E-transfer", "Cash", "Card", "Cheque", "Bank transfer", "Other"];
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
function DepositRecorder({ appId, clientId, fees, caseInvoice, onSaved }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [proof, setProof] = useState(emptyProof());
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("E-transfer");
  const [saving, setSaving] = useState(false);
  const cur = fees.currency || "CAD";
  const record = async () => {
    const n = parseFloat(amount);
    if (!(n > 0)) { toast.error("Enter the deposit amount received"); return; }
    const problem = proofProblem(proof, isAdmin);
    if (problem) { toast.error(problem); return; }
    if (fees.balance !== null && n > fees.balance + 0.001 && !window.confirm(`This is more than the remaining balance (${cur} ${fees.balance.toFixed(2)}). Record anyway?`)) return;
    setSaving(true);
    try {
      let inv = caseInvoice;
      if (!inv) inv = (await api.post("/invoices", { application_id: appId, client_id: clientId })).data;
      const data = await submitPayment({ invoiceId: inv.id, clientId, body: { amount: n, method, note: "Deposit (recorded from retainer)" }, proof });
      if (!data.pending_approval) toast.success("Deposit recorded — retainer deposit and balance updated");
      setAmount(""); setProof(emptyProof()); onSaved();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };
  return (
    <div className="border-t border-slate-100 pt-3 space-y-2" data-testid="deposit-recorder">
      <div className="flex items-center justify-between text-[11px]">
        <span className="font-semibold text-slate-500 uppercase tracking-wide">Deposit received</span>
        <span className="text-slate-500">Received so far: <b className="text-emerald-600" data-testid="deposit-received-total">{cur} ${Number(fees.paid || 0).toFixed(2)}</b></span>
      </div>
      <div className="flex gap-2">
        <input data-testid="deposit-amount" type="number" min="0" step="0.01" placeholder="Amount" value={amount} onChange={(e) => setAmount(e.target.value)} className="flex-1 min-w-0 px-2.5 py-2 rounded-lg border border-slate-200 text-sm" />
        <select data-testid="deposit-method" value={method} onChange={(e) => setMethod(e.target.value)} className="px-2 py-2 rounded-lg border border-slate-200 text-sm">
          {PAY_METHODS.map((m) => <option key={m}>{m}</option>)}
        </select>
        <button data-testid="deposit-record" onClick={record} disabled={saving} className="px-3 py-2 rounded-lg bg-secondary text-white text-xs font-semibold disabled:opacity-50 inline-flex items-center gap-1"><DollarSign className="h-3.5 w-3.5" /> {saving ? "Saving…" : (!proof.file && !isAdmin ? "Send for approval" : "Record")}</button>
      </div>
      <PaymentProofPicker value={proof} onChange={setProof} isAdmin={isAdmin} testPrefix="deposit" />
    </div>
  );
}
